/**
 * L'HABILLAGE COMMUN DES GRAPHIQUES DU JEU.
 *
 * Les graphes sont projetés dans une salle, au milieu de cartes blanches à la
 * palette Hydros. Les réglages par défaut de chart.js — grilles quadrillées,
 * bordures noires, légendes en carrés, infobulles grises — viennent d'ailleurs
 * et se voient. Ce module tient l'habillage EN UN SEUL ENDROIT : un graphe de
 * plus s'y branche en trois lignes, et changer la teinte des axes ne demande
 * pas d'ouvrir dix fichiers.
 *
 * Il ne décide de rien d'autre. Les couleurs qui PORTENT DU SENS restent chez
 * qui les nomme — les secteurs dans `acgSummary.SECTOR_COLORS`, par exemple.
 * `CATEGORICAL` n'est là que pour les séries dont aucune table ne dit la
 * couleur (les familles de leviers), et elle est ordonnée : les teintes les
 * plus lisibles d'abord, pour que les deux premières parts soient les mieux
 * séparées.
 */

export const CHART_FONT = "'Geist', system-ui, -apple-system, sans-serif";

export const CHART_INK = '#205673';      // texte fort (valeurs, titres)
export const CHART_MUTED = '#8fa3ae';    // graduations, légendes
export const CHART_GRID = '#eef3f5';     // lignes de fond
export const CHART_SURFACE = '#ffffff';  // le fond des cartes, qui sert de séparateur

/**
 * Palette qualitative, dérivée de la palette Hydros : les bleus d'abord, puis
 * les accents chauds. DOUZE teintes, parce que le donut des usages d'eau peut
 * en demander autant — une palette plus courte se remettrait à tourner, et deux
 * parts voisines de la même couleur ne se distinguent plus du tout.
 */
export const CATEGORICAL = [
  '#205673', '#3587a1', '#54a8ba', '#8fcfd2',
  '#db6c52', '#eda480', '#c9b6d8', '#a7b8c2',
  '#2f7f6b', '#7fb069', '#f2c14e', '#8c6b8f',
];

export const categoricalColor = (index) => CATEGORICAL[index % CATEGORICAL.length];

/** Légende à pastilles rondes, alignée à gauche : elle se lit comme une liste. */
export const legend = (overrides = {}) => ({
  display: true,
  position: 'top',
  align: 'start',
  labels: {
    usePointStyle: true,
    pointStyle: 'circle',
    boxWidth: 8,
    boxHeight: 8,
    padding: 16,
    color: CHART_MUTED,
    font: { family: CHART_FONT, size: 12, weight: '600' },
  },
  ...overrides,
});

/** Infobulle sombre et arrondie, à la place du gris translucide par défaut. */
export const tooltip = (overrides = {}) => ({
  backgroundColor: '#0f2d3d',
  borderColor: 'rgba(255, 255, 255, 0.12)',
  borderWidth: 1,
  cornerRadius: 10,
  padding: 12,
  boxPadding: 6,
  usePointStyle: true,
  titleFont: { family: CHART_FONT, size: 13, weight: '700' },
  bodyFont: { family: CHART_FONT, size: 12.5 },
  ...overrides,
});

/** Axe des catégories : pas de grille, pas de trait d'axe, juste les libellés. */
export const categoryAxis = (overrides = {}) => ({
  grid: { display: false },
  border: { display: false },
  ticks: {
    color: CHART_MUTED,
    font: { family: CHART_FONT, size: 12, weight: '600' },
  },
  ...overrides,
});

/** Axe des valeurs : des repères horizontaux clairs, et rien de plus. */
export const valueAxis = (overrides = {}) => ({
  beginAtZero: true,
  grid: { color: CHART_GRID, drawTicks: false },
  border: { display: false, dash: [] },
  ticks: {
    color: CHART_MUTED,
    padding: 10,
    font: { family: CHART_FONT, size: 12 },
  },
  ...overrides,
});
